package com.eladfinish.bible.contest.ui.layout

import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/** Lines of Hebrew text stop being comfortable to read, and buttons to reach, beyond this width. */
val ReadableContentMaxWidth: Dp = 700.dp

private val ShortWindowMaxHeight: Dp = 500.dp

private val DialogHeightAboveKeyboard: Dp = 120.dp

/**
 * Fills the width up to [max] and centers what is left over, for content made for a narrow column.
 *
 * The first fillMaxWidth() gives the centering its full width: in a parent that does not force a
 * width, wrapContentWidth() would shrink to the content and the parent would put it at the side.
 * widthIn() must come before the second fillMaxWidth(): a modifier chain applies outside-in during
 * measurement, so the other order would lock the content to the full window width.
 */
fun Modifier.readableWidth(max: Dp = ReadableContentMaxWidth): Modifier =
    this
        .fillMaxWidth()
        .wrapContentWidth(Alignment.CenterHorizontally)
        .widthIn(max = max)
        .fillMaxWidth()

/**
 * Whether this window is a phone in landscape: too short for a screen's controls above its content,
 * but wide enough to put them side by side.
 *
 * Reads the window and not the constraints, which shrink when the keyboard opens: the layout
 * must not change, and the focused text field must not move, each time the keyboard shows or hides.
 */
@Composable
fun isShortWideWindow(): Boolean {
    val size = LocalWindowInfo.current.containerSize
    return with(LocalDensity.current) {
        isShortWideWindow(width = size.width.toDp(), height = size.height.toDp())
    }
}

/**
 * The keyboard is open on a phone in landscape, and leaves about 150dp for the whole app: the toolbar
 * and the ad banner give way to what is being typed, until the keyboard closes.
 */
@Composable
fun isKeyboardCoveringShortWindow(): Boolean = isShortWideWindow() && isKeyboardOpen()

/**
 * For a dialog with a text field: its window does not stop at the keyboard, so on a phone in landscape
 * the card would run behind it, with its buttons out of reach. The content inside must scroll.
 */
@Composable
fun Modifier.heightAboveKeyboard(): Modifier =
    if (isKeyboardCoveringShortWindow()) heightIn(max = DialogHeightAboveKeyboard) else this

/**
 * Whether the keyboard is open, read once in the app's main window. A dialog is a window of its own,
 * where the keyboard's inset stays 0, but it does get this value from the composition.
 */
val LocalIsKeyboardOpen = compositionLocalOf { false }

/** isImeVisible exists on Android only: the inset's height works on every platform. */
@Composable
fun rememberIsKeyboardOpen(): Boolean {
    val ime = WindowInsets.ime
    val density = LocalDensity.current
    val isOpen by remember(ime, density) { derivedStateOf { ime.getBottom(density) > 0 } }
    return isOpen
}

@Composable
fun isKeyboardOpen(): Boolean = LocalIsKeyboardOpen.current

internal fun isShortWideWindow(width: Dp, height: Dp): Boolean =
    height < ShortWindowMaxHeight && width > height
